import * as d3 from 'd3';
import React, { useEffect, useState } from 'react';
import { Checkbox } from 'react-bootstrap';
import SubHeader from '../../SubHeader.jsx';
import Chart from './Chart.js';
import UnitsChart from './UnitsChart.js';
import MileMarkersChart from './MileMarkersChart.js';
import TimelineScaleTypeSelect from './TimelineScaleTypeSelect.jsx';
import { getOrgTermsContent } from '../../../content/utils.js';
import styles from './index.module.css';
import withFullscreen from '../../common/withFullscreen.jsx';

const GanttChartV4 = ({
  results,
  milestones,
  onShowMilestoneModal,
  onToggleFullscreen,
  isFullscreen,
  onScaleTypeChange,
  chartContainerId = 'ganttExportContainer',
}) => {
  const [charts, setCharts] = useState(null);
  const [exportCharts, setExportCharts] = useState(null);
  const [timelineOptions, setTimelineOptions] = useState({
    scaleType: 'quarter',
    showMilestones: false,
    chartStartDate: Date.now(),
  });

  const { scaleType, showMilestones, chartStartDate } = timelineOptions;

  useEffect(() => {
    if (charts) {
      updateChartsOptions(
        'minHeight',
        isFullscreen ? window.innerHeight - 120 : 300
      );
      updateChartsOptions('width', window.innerWidth - 1);
      callChartsActions('rebuild');
    }
  }, [isFullscreen]);

  const onUpdateReportOptions = (values) => {
    setTimelineOptions({
      ...timelineOptions,
      ...values,
    });
  };

  const groupMilestonesByDate = (arr) => {
    const groupedMilestones = {};

    arr.forEach((milestone) => {
      const date_at = new Date(milestone.date_at);
      const dateKey = date_at.toISOString().split('T')[0];

      if (!groupedMilestones[dateKey]) {
        groupedMilestones[dateKey] = [];
      }

      groupedMilestones[dateKey].push(milestone);
    });

    return Object.entries(groupedMilestones).map(
      ([date_at, childMilestone]) => ({
        date_at,
        childMilestone,
      })
    );
  };

  const sortByStartDate = (arr) =>
    arr.sort((a, b) => new Date(a.startDate) - new Date(b.startDate));

  const getData = (arr) => {
    return sortByStartDate(arr).map((project) => ({
      project: { id: project.id },
      title: project.name,
      startDate: project.startDate,
      endDate: project.endDate,
      impact: project.impact,
    }));
  };

  const updateChartsOptions = (key, val) => {
    charts &&
      charts.forEach((chart) => {
        chart.options[key] = val;
      });
  };

  const callChartsActions = (key) => {
    charts?.forEach((chart) => chart[key]());
  };

  const getOffsetData = () => {
    switch (scaleType) {
      case 'week':
        return { key: 'timeWeek', steps: 1 };
      case 'month':
        return { key: 'timeMonth', steps: 1 };
      case 'quarter':
        return { key: 'timeMonth', steps: 3 };
      case 'year':
        return { key: 'timeMonth', steps: 12 };
      default:
        return { key: 'timeWeek', steps: 2 };
    }
  };

  const current = () => {
    const newDate = Date.now();
    onUpdateReportOptions({ chartStartDate: newDate });
    updateChartsOptions('startDate', newDate);
    callChartsActions('update');
  };

  const prev = () => {
    const offsetData = getOffsetData();
    const newDate = Date.parse(
      d3[offsetData.key].offset(chartStartDate, -offsetData.steps)
    );
    onUpdateReportOptions({ chartStartDate: newDate });
    updateChartsOptions('startDate', newDate);
    callChartsActions('update');
  };

  const next = () => {
    const offsetData = getOffsetData();
    const newDate = Date.parse(
      d3[offsetData.key].offset(chartStartDate, offsetData.steps)
    );
    onUpdateReportOptions({ chartStartDate: newDate });
    updateChartsOptions('startDate', newDate);
    callChartsActions('update');
  };

  const year = () => {
    onUpdateReportOptions({ scaleType: 'year' });
    updateChartsOptions('scaleType', 'year');
    updateChartsOptions('monthCount', 48);
    callChartsActions('rebuild');
    onScaleTypeChange?.('year');
  };

  const quarter = () => {
    onUpdateReportOptions({ scaleType: 'quarter' });
    updateChartsOptions('scaleType', 'quarter');
    updateChartsOptions('monthCount', 12);
    callChartsActions('rebuild');
    onScaleTypeChange?.('quarter');
  };

  const month = () => {
    onUpdateReportOptions({ scaleType: 'month' });
    updateChartsOptions('scaleType', 'month');
    updateChartsOptions('monthCount', 4);
    callChartsActions('rebuild');
    onScaleTypeChange?.('month');
  };

  const week = () => {
    onUpdateReportOptions({ scaleType: 'week' });
    updateChartsOptions('scaleType', 'week');
    callChartsActions('rebuild');
    onScaleTypeChange?.('week');
  };

  const toggleShowMilestones = () => {
    onUpdateReportOptions({ showMilestones: !showMilestones });
    updateChartsOptions('showMilestones', !showMilestones);
    callChartsActions('rebuild');
  };

  const resizeChart = () => {
    updateChartsOptions('width', document.body.clientWidth - 1);
    callChartsActions('rebuild');
  };

  useEffect(() => {
    const monthCount = {
      week: 4,
      month: 4,
      quarter: 12,
      year: 48,
    };

    const options = {
      width: document.body.clientWidth - 1,
      padding: 10,
      barHeight: 24,
      weekCount: 4,
      monthCount: monthCount[scaleType],
      startDate: chartStartDate,
      scaleType,
      showMilestones,
      minHeight: 300,
    };

    const uniqueProjectIds = results.map((project) => project.id);
    const data = getData(results);
    const milestonesFromAllCharts = groupMilestonesByDate(milestones);

    const unitsChart = new UnitsChart('#ganttChart-units', [], options);
    const mileMarkersChart = new MileMarkersChart(
      '#ganttChart-milestones',
      { milestonesFromAllCharts },
      { ...options, onShowMilestoneModal }
    );
    const chart = new Chart(
      '#ganttChart',
      { rows: data, lines: milestonesFromAllCharts },
      { ...options, uniqueProjectIds }
    );

    setCharts([unitsChart, mileMarkersChart, chart]);
  }, [results]);

  useEffect(() => {
    if (charts) {
      callChartsActions('build');
    }
  }, [charts]);

  useEffect(() => {
    if (charts) {
      current();
    }
  }, [scaleType]);

  useEffect(() => {
    const PIXELS_PER_MONTH = { week: 700, month: 200, quarter: 100, year: 50 };
    const PADDING_MONTHS = { week: 2, month: 2, quarter: 2, year: 6 };
    const MIN_EXPORT_WIDTH = 1200;
    const MAX_EXPORT_WIDTH = 8000;
    const SENTINEL_MIN = new Date('1990-01-01').getTime();
    const SENTINEL_MAX = new Date('2070-01-01').getTime();
    const PADDING = {
      week: { unit: 'timeWeek', count: 1 },
      month: { unit: 'timeMonth', count: 1 },
      quarter: { unit: 'timeMonth', count: 2 },
      year: { unit: 'timeMonth', count: 6 },
    };

    const monthCount = {
      week: 4,
      month: 4,
      quarter: 12,
      year: 48,
    };

    const baseOptions = {
      padding: 10,
      barHeight: 24,
      weekCount: 4,
      monthCount: monthCount[scaleType],
      scaleType,
      showMilestones,
      minHeight: 300,
    };

    const uniqueProjectIds = results.map((project) => project.id);
    const data = getData(results);
    const milestonesFromAllCharts = groupMilestonesByDate(milestones);

    const validRows = data.filter((r) => r.startDate && r.endDate);
    const now = Date.now();

    const milestoneDates = milestonesFromAllCharts
      .map((m) => new Date(m.date_at).getTime())
      .filter((t) => !Number.isNaN(t) && t > SENTINEL_MIN && t < SENTINEL_MAX);

    const rowDates = validRows.length
      ? [
          Math.min(...validRows.map((r) => new Date(r.startDate).getTime())),
          Math.max(...validRows.map((r) => new Date(r.endDate).getTime())),
        ]
      : [now, now];

    const rawMin = new Date(Math.min(rowDates[0], ...milestoneDates));
    const rawMax = new Date(Math.max(rowDates[1], ...milestoneDates));

    const pad = PADDING[scaleType] ?? { unit: 'timeMonth', count: 1 };
    const minDate = d3[pad.unit].offset(rawMin, -pad.count);
    const maxDate = d3[pad.unit].offset(rawMax, pad.count);

    const totalMonths =
      Math.ceil((maxDate - minDate) / (1000 * 60 * 60 * 24 * 30)) + 1;
    const totalWeeks =
      Math.ceil((maxDate - minDate) / (1000 * 60 * 60 * 24 * 7)) + 1;

    const computedWidth = totalMonths * (PIXELS_PER_MONTH[scaleType] || 100);
    const exportWidth = Math.min(
      Math.max(computedWidth, MIN_EXPORT_WIDTH),
      MAX_EXPORT_WIDTH
    );

    const exportOptions = {
      ...baseOptions,
      width: exportWidth,
      monthCount: totalMonths,
      weekCount: totalWeeks,
      startDate: minDate.getTime(),
    };

    const unitsExport = new UnitsChart(
      '#ganttChart-units-export',
      [],
      exportOptions
    );
    const mileMarkersExport = new MileMarkersChart(
      '#ganttChart-milestones-export',
      { milestonesFromAllCharts },
      { ...exportOptions, onShowMilestoneModal }
    );
    const chartExport = new Chart(
      '#ganttChartExport',
      { rows: data, lines: milestonesFromAllCharts },
      { ...exportOptions, uniqueProjectIds }
    );

    setExportCharts([unitsExport, mileMarkersExport, chartExport]);
  }, [results, scaleType, showMilestones]);

  useEffect(() => {
    if (exportCharts) exportCharts.forEach((c) => c.build());
  }, [exportCharts]);

  window.addEventListener('resize', resizeChart);

  return (
    <section className={isFullscreen ? 'fullscreen' : ''}>
      <SubHeader flexJustify small>
        <div className={`flex ${styles.flex}`}>
          <h2>{getOrgTermsContent('portfolio')} Timeline</h2>
          <div className="gantt-pager header-item">
            <button className="page prev" onClick={prev}>
              Previous
            </button>
            <button className="page today" onClick={current}>
              Today
            </button>
            <button className="page next" onClick={next}>
              Next
            </button>
          </div>
        </div>
        <div className={`flex flex-align-center ${styles.flex}`}>
          <div className="m-r-18">
            <Checkbox
              className="label-left"
              checked={isFullscreen}
              onChange={onToggleFullscreen}
            >
              <span className="text-muted font-size-14">Fullscreen</span>
              <span className="checkmark"></span>
            </Checkbox>
          </div>
          <div className="m-r-18">
            <Checkbox
              className="label-left"
              checked={showMilestones}
              onChange={toggleShowMilestones}
            >
              <span className="text-muted font-size-14">Show Milestones</span>
              <span className="checkmark"></span>
            </Checkbox>
          </div>
          <div className="m-r-18">
            <TimelineScaleTypeSelect
              scaleType={scaleType}
              onSelect={{ week, month, quarter, year }}
            />
          </div>
        </div>
      </SubHeader>

      <div className="ganttWrapper">
        <div id="ganttChart-units" style={{ display: 'inline-flex' }} />
      </div>
      <div className="ganttWrapper">
        <div id="ganttChart-milestones" style={{ display: 'inline-flex' }} />
      </div>
      <div className="ganttWrapper">
        <div id="tooltip" className="tooltip">
          <div id="type" />
          <h3 id="title" />
          <strong id="category" />
        </div>
        <div id="ganttChart" style={{ display: 'inline-flex' }} />
      </div>

      <div
        style={{
          position: 'fixed',
          top: '-9999px',
          left: '-9999px',
          visibility: 'hidden',
          pointerEvents: 'none',
        }}
      >
        <div id={chartContainerId}>
          <div className="ganttWrapper">
            <div
              id="ganttChart-units-export"
              style={{ display: 'inline-flex' }}
            />
          </div>
          <div className="ganttWrapper">
            <div
              id="ganttChart-milestones-export"
              style={{ display: 'inline-flex' }}
            />
          </div>
          <div className="ganttWrapper">
            <div id="ganttChartExport" style={{ display: 'inline-flex' }} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default withFullscreen(GanttChartV4, { defaultHeight: 300 });
